/**
 * Ba nhãn nhắc trên đơn (spec 2026-08-23 mục 4.4), CEO duyệt 24/08/2026.
 *
 * Chúng CHỈ NHẮC — không nhãn nào chặn thao tác nào, và không nhãn nào là cột
 * lưu trong bảng: máy chủ tính lúc đọc. Nếu một ngày có người muốn dùng nhãn
 * làm điều kiện chặn, đó là đổi quyết định chứ không phải mở rộng.
 *
 * Không có dữ liệu (chưa hỏi xong / hỏi hỏng) thì KHÔNG vẽ gì. Cố ý không vẽ ô
 * xám chờ: nhãn nằm xen giữa các chip khác trên cùng một dòng, nên một ô nhấp
 * nháy mỗi lần đổi trang gây rối hơn là giúp.
 */

import { formatVNDCompact } from "@/lib/format";

import { closureChips, type OrderClosure } from "../closure";

export type AttentionFlags = {
  /** Tên các bạn makeup chưa nộp ảnh nghiệm thu. */
  makeupProofMissing?: string[];
  /** Tiền khách còn nợ, đã trừ các phiếu ĐÃ xác nhận. */
  debtRemaining: number;
  /** Đơn còn nợ nhưng người xem không được thấy số tiền (QĐ 0132). */
  debtAmountHidden?: boolean | undefined;
  /** Còn khâu hậu kiểm chưa làm. */
  postCheckPending: boolean;
  /** Bảng kiểm khép đơn (QĐ 0292) — chỉ có ở đơn đã giao. */
  closure?: OrderClosure | undefined;
};

type Item = { key: string; tone: "warn" | "danger" | "info"; label: string; title?: string };

const TONE_CLASS: Record<Item["tone"], string> = {
  danger: "bg-red-100 text-red-700 border border-red-200",
  warn: "bg-amber-100 text-amber-800 border border-amber-200",
  info: "bg-sky-100 text-sky-800 border border-sky-200",
};
const TONE_DOT: Record<Item["tone"], string> = { danger: "🔴", warn: "🟡", info: "🔵" };

/** Tách khỏi phần vẽ để bài kiểm soi được LUẬT mà không phải dựng DOM. */
export function attentionItems(flags: AttentionFlags | undefined): Item[] {
  if (!flags) return [];
  const items: Item[] = [];
  const missing = flags.makeupProofMissing ?? [];
  if (missing.length > 0) {
    items.push({
      key: "makeup",
      tone: "warn",
      label: `Thiếu ảnh makeup (${missing.length})`,
      title: `Chưa có ảnh nghiệm thu: ${missing.join(", ")}`,
    });
  }
  // Chỉ nợ DƯƠNG mới là nợ. Khách trả dư (số âm) là chuyện của kế toán, không
  // phải chuyện nhắc trên danh sách đơn — và một nhãn đỏ "còn nợ -50.000đ" thì
  // vừa sai vừa làm người đọc mất tin vào mọi nhãn khác.
  if (flags.debtRemaining > 0) {
    items.push({
      key: "debt",
      tone: "danger",
      label: `Còn nợ ${formatVNDCompact(flags.debtRemaining)}`,
    });
  } else if (flags.debtAmountHidden) {
    // Người không được xem tiền của đơn: vẫn biết đơn còn nợ, không thấy số.
    items.push({ key: "debt", tone: "danger", label: "Còn nợ" });
  }
  if (flags.postCheckPending) {
    items.push({ key: "post", tone: "warn", label: "Chưa hậu kiểm" });
  }
  // Đơn đã giao còn thiếu gì để khép (QĐ 0292). Nợ không lặp ở đây — nhãn đỏ
  // "Còn nợ" phía trên đã nói.
  for (const chip of closureChips(flags.closure)) {
    items.push({ key: `closure-${chip.key}`, tone: chip.tone, label: chip.label, title: `Còn thiếu để khép đơn: ${chip.label}` });
  }
  return items;
}

export function OrderAttentionBadges({
  flags,
  onPostCheckClick,
}: {
  flags?: AttentionFlags | undefined;
  /**
   * Có thì nhãn "Chưa hậu kiểm" thành nút dẫn tới chỗ chấm (màn chi tiết đơn,
   * CEO chốt 1a ngày 01/10/2026). Danh sách đơn không truyền — ở đó nhãn chỉ
   * nhắc.
   */
  onPostCheckClick?: () => void;
}) {
  const items = attentionItems(flags);
  if (items.length === 0) return null;

  return (
    <>
      {items.map((it) => {
        const className = `shrink-0 rounded px-1.5 py-px text-[10px] font-semibold ${TONE_CLASS[it.tone]}`;
        const text = `${TONE_DOT[it.tone]} ${it.label}`;
        if (it.key === "post" && onPostCheckClick) {
          return (
            <button
              key={it.key}
              type="button"
              title="Bấm để tới chỗ chấm hậu kiểm ảnh gốc"
              onClick={onPostCheckClick}
              className={`${className} cursor-pointer underline decoration-dotted underline-offset-2 hover:bg-amber-200`}
            >
              {text} ↓
            </button>
          );
        }
        return (
          <span key={it.key} title={it.title ?? it.label} className={className}>
            {text}
          </span>
        );
      })}
    </>
  );
}
